import { auth } from "../../configs/firebaseConfig";
import { resolveActiveFunctionUrl } from "../../configs/runtimeEnvironment";

function resolveMergeTicketUrl(kind) {
  if (kind === "create") {
    return resolveActiveFunctionUrl("createLettuceTalkMergeTicket", {
      devUrl: process.env.REACT_APP_CREATE_LETTUCE_TALK_MERGE_TICKET_URL_DEV,
      prodUrl: process.env.REACT_APP_CREATE_LETTUCE_TALK_MERGE_TICKET_URL_PROD,
    });
  }

  return resolveActiveFunctionUrl("redeemLettuceTalkMergeTicket", {
    devUrl: process.env.REACT_APP_REDEEM_LETTUCE_TALK_MERGE_TICKET_URL_DEV,
    prodUrl: process.env.REACT_APP_REDEEM_LETTUCE_TALK_MERGE_TICKET_URL_PROD,
  });
}

const AUTH_SETTLE_TIMEOUT_MS = 1500;
const AUTH_SETTLE_INTERVAL_MS = 50;

function sleep(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

function isExpectedUser(user, { expectedUid, requirePermanent } = {}) {
  if (!user) {
    return false;
  }

  if (expectedUid && user.uid !== expectedUid) {
    return false;
  }

  if (requirePermanent && user.isAnonymous === true) {
    return false;
  }

  return true;
}

async function getInitializedCurrentUser(options = {}) {
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  }

  const immediateUser = auth.currentUser || null;

  if (!options.expectedUid && !options.requirePermanent) {
    return immediateUser;
  }

  if (isExpectedUser(immediateUser, options)) {
    return immediateUser;
  }

  if (
    options.expectedUid &&
    immediateUser &&
    immediateUser.isAnonymous !== true &&
    immediateUser.uid !== options.expectedUid
  ) {
    return immediateUser;
  }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    await Promise.resolve();

    if (isExpectedUser(auth.currentUser || null, options)) {
      return auth.currentUser;
    }
  }

  const startedAt = Date.now();

  while (Date.now() - startedAt < AUTH_SETTLE_TIMEOUT_MS) {
    await sleep(AUTH_SETTLE_INTERVAL_MS);

    if (isExpectedUser(auth.currentUser || null, options)) {
      return auth.currentUser;
    }
  }

  return auth.currentUser || null;
}

function cleanString(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

async function parseResponseJson(response) {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
}

function buildFunctionError(data, fallbackMessage, response) {
  const error = new Error(data.message || data.error || fallbackMessage);

  error.status = response.status;
  error.code = data.code || data.error;
  error.reason = data.reason;
  error.details = data.details || "";

  return error;
}

function buildClientError(message, code) {
  const error = new Error(message);

  error.code = code;

  return error;
}

function validateCreateMergeTicketResponse(data) {
  if (
    data?.success === false ||
    !cleanString(data?.ticketId)
  ) {
    throw buildClientError(
      "Invalid create merge ticket response",
      "invalid-create-merge-ticket-response"
    );
  }

  return {
    ...data,
    success: true,
    ticketId: cleanString(data.ticketId),
  };
}

function validateRedeemMergeTicketResponse(data) {
  if (data?.success !== true) {
    throw buildClientError(
      "Invalid redeem merge ticket response",
      "invalid-redeem-merge-ticket-response"
    );
  }

  return data;
}

export async function createLettuceTalkMergeTicket({
  targetEmail,
  cardId,
  generationId,
}) {
  const url = resolveMergeTicketUrl("create");
  const user = await getInitializedCurrentUser();

  if (!user || user.isAnonymous !== true) {
    const error = new Error("Anonymous card session required");
    error.code = "missing-anonymous-user";
    throw error;
  }

  const email = cleanString(targetEmail);
  const safeCardId = cleanString(cardId);
  const safeGenerationId = cleanString(generationId);

  if (!email) {
    const error = new Error("Google account email required");
    error.code = "missing-target-email";
    throw error;
  }

  if (!safeCardId && !safeGenerationId) {
    const error = new Error("Card or generation ID required");
    error.code = "missing-merge-source";
    throw error;
  }

  if (!url) {
    const error = new Error("Missing create LettuceTalk merge ticket function URL");
    error.code = "missing-create-merge-ticket-url";
    throw error;
  }

  const idToken = await user.getIdToken();
  const body = {
    targetEmail: email,
  };

  if (safeCardId) {
    body.cardId = safeCardId;
  } else {
    body.generationId = safeGenerationId;
  }

  let response;

  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
      mode: "cors",
    });
  } catch (fetchError) {
    const error = new Error("Failed to reach create merge ticket function");
    error.code = "network-request-failed";
    throw error;
  }
  const data = await parseResponseJson(response);

  if (!response.ok) {
    throw buildFunctionError(
      data,
      "Failed to create LettuceTalk merge ticket",
      response
    );
  }

  return validateCreateMergeTicketResponse(data);
}

export async function redeemLettuceTalkMergeTicket({ ticketId, expectedUid }) {
  const url = resolveMergeTicketUrl("redeem");
  const safeExpectedUid = cleanString(expectedUid);
  const user = await getInitializedCurrentUser({
    expectedUid: safeExpectedUid,
    requirePermanent: true,
  });

  if (!user || user.isAnonymous === true) {
    const error = new Error("Google account required");
    error.code = "missing-google-user";
    throw error;
  }

  if (safeExpectedUid && user.uid !== safeExpectedUid) {
    const error = new Error("Expected Google account required");
    error.code = "google-user-mismatch";
    throw error;
  }

  const safeTicketId = cleanString(ticketId);

  if (!safeTicketId) {
    const error = new Error("Merge ticket ID required");
    error.code = "missing-ticket-id";
    throw error;
  }

  if (!url) {
    const error = new Error("Missing redeem LettuceTalk merge ticket function URL");
    error.code = "missing-redeem-merge-ticket-url";
    throw error;
  }

  const idToken = await user.getIdToken();
  let response;

  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ ticketId: safeTicketId }),
      mode: "cors",
    });
  } catch (fetchError) {
    const error = new Error("Failed to reach redeem merge ticket function");
    error.code = "network-request-failed";
    throw error;
  }
  const data = await parseResponseJson(response);

  if (!response.ok) {
    throw buildFunctionError(
      data,
      "Failed to redeem LettuceTalk merge ticket",
      response
    );
  }

  return validateRedeemMergeTicketResponse(data);
}
